/**
 * MemberList Component
 *
 * Displays a list of household members with actions for the owner.
 * Shows avatar, name, email, role, and joined date for each member.
 */

import { useState } from 'react';
import { cn } from '@/utils/cn';
import { Button, Spinner } from '@/components/ui';
import type { HouseholdMember, HouseholdRole } from '@/types/household';

export interface MemberListProps {
  members: HouseholdMember[];
  isLoading?: boolean;
  currentUserRole: HouseholdRole;
  onRemoveMember?: (userId: string, memberName: string | null) => void;
  onTransferOwnership?: (userId: string, memberName: string | null) => void;
}

/**
 * Get initials from a name or email
 */
function getInitials(name: string | null, email: string): string {
  if (name) {
    const parts = name.trim().split(/\s+/);
    const firstPart = parts[0];
    const lastPart = parts[parts.length - 1];
    if (parts.length >= 2 && firstPart && lastPart && firstPart[0] && lastPart[0]) {
      return (firstPart[0] + lastPart[0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  }
  return email.substring(0, 2).toUpperCase();
}

/**
 * Format a date string as relative time
 */
function formatJoinedDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return 'Today';
  } else if (diffDays === 1) {
    return 'Yesterday';
  } else if (diffDays < 7) {
    return `${diffDays} days ago`;
  } else if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
  } else if (diffDays < 365) {
    const months = Math.floor(diffDays / 30);
    return `${months} month${months > 1 ? 's' : ''} ago`;
  } else {
    return date.toLocaleDateString();
  }
}

export default function MemberList({
  members,
  isLoading = false,
  currentUserRole,
  onRemoveMember,
  onTransferOwnership,
}: MemberListProps) {
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const isOwner = currentUserRole === 'owner';

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (members.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <p>No members found.</p>
      </div>
    );
  }

  return (
    <div className="divide-y divide-gray-200">
      {members.map((member) => (
        <div
          key={member.userId}
          className={cn(
            'flex items-center justify-between py-4 px-4 -mx-4',
            member.isCurrentUser && 'bg-gray-50'
          )}
        >
          <div className="flex items-center gap-4">
            {/* Avatar */}
            <div
              className={cn(
                'w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium',
                member.role === 'owner'
                  ? 'bg-primary-100 text-primary-700'
                  : 'bg-gray-100 text-gray-700'
              )}
              aria-hidden="true"
            >
              {getInitials(member.name, member.email)}
            </div>

            {/* Name and email */}
            <div>
              <div className="flex items-center gap-2">
                <span className="font-medium text-gray-900">
                  {member.name || member.email.split('@')[0]}
                </span>
                {member.isCurrentUser && (
                  <span className="text-xs text-gray-500">(you)</span>
                )}
                {member.role === 'owner' && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-700">
                    Owner
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-500">{member.email}</p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            {/* Joined date */}
            <span className="text-sm text-gray-500 hidden sm:block">
              Joined {formatJoinedDate(member.joinedAt)}
            </span>

            {/* Actions menu (owner only, not for self or other owner) */}
            {isOwner && !member.isCurrentUser && member.role !== 'owner' && (
              <div className="relative">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    setOpenMenuId(openMenuId === member.userId ? null : member.userId)
                  }
                  aria-haspopup="menu"
                  aria-expanded={openMenuId === member.userId}
                  aria-label={`Actions for ${member.name || member.email}`}
                >
                  <svg
                    className="w-5 h-5 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
                    />
                  </svg>
                </Button>

                {openMenuId === member.userId && (
                  <>
                    {/* Backdrop to close menu */}
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setOpenMenuId(null)}
                      aria-hidden="true"
                    />

                    {/* Dropdown menu */}
                    <div
                      className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-20"
                      role="menu"
                      aria-orientation="vertical"
                    >
                      <div className="py-1">
                        <button
                          onClick={() => {
                            setOpenMenuId(null);
                            onTransferOwnership?.(member.userId, member.name);
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
                          role="menuitem"
                        >
                          <span className="flex items-center gap-2">
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              aria-hidden="true"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                              />
                            </svg>
                            Make Owner
                          </span>
                        </button>
                        <button
                          onClick={() => {
                            setOpenMenuId(null);
                            onRemoveMember?.(member.userId, member.name);
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 focus:bg-red-50 focus:outline-none"
                          role="menuitem"
                        >
                          <span className="flex items-center gap-2">
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              aria-hidden="true"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                              />
                            </svg>
                            Remove
                          </span>
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
